"use client";

import { useState, useRef } from "react";
import { getAuthHeaders } from "@/lib/auth-client";

type Model = "sdxl-turbo" | "sdxl-base";
type Size = { label: string; w: number; h: number };

const SIZES: Size[] = [
  { label: "512 × 512", w: 512, h: 512 },
  { label: "768 × 768", w: 768, h: 768 },
  { label: "512 × 768", w: 512, h: 768 },
  { label: "768 × 512", w: 768, h: 512 },
];

type Result = { url: string; prompt: string; model: Model; ts: number };

const PROMPT_SUGGESTIONS = [
  "A serene Japanese garden at sunrise with cherry blossoms and a koi pond",
  "Futuristic cityscape at night with neon lights reflecting on wet streets",
  "A cozy coffee shop interior with warm lighting, wooden tables and books",
  "Studio product photo of a sleek black smartwatch on marble surface",
  "A golden retriever puppy playing in autumn leaves, soft bokeh background",
  "Minimalist logo design concept: abstract geometric shapes, deep blue palette",
];

export default function GeneratePage() {
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState<Model>("sdxl-turbo");
  const [size, setSize] = useState<Size>(SIZES[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<Result[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleGenerate = async () => {
    if (!prompt.trim() || loading) return;
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/proxy/generate-product-image/", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify({
          product_name: prompt.trim().slice(0, 80),
          prompt: prompt.trim(),
          model,
          width: size.w,
          height: size.h,
          save_to_qdrant: false,
        }),
      });
      const data = await res.json().catch(() => ({})) as { url?: string; detail?: string; error?: string };
      if (!res.ok || !data.url) {
        setError(
          typeof data.detail === "string"
            ? data.detail
            : data.error ?? `Generation failed (${res.status})`
        );
        return;
      }
      setResults((prev) => [{ url: data.url!, prompt: prompt.trim(), model, ts: Date.now() }, ...prev]);
    } catch {
      setError("Could not reach the image generation service.");
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestion = (s: string) => {
    setPrompt(s);
    textareaRef.current?.focus();
  };

  const handleDownload = (url: string, idx: number) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = `generated-${idx + 1}.png`;
    a.target = "_blank";
    a.click();
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50/30">
      <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">

        {/* Header */}
        <div className="mb-8 text-center">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-violet-200 bg-violet-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-violet-600">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
            AI Powered
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Image Generator
          </h1>
          <p className="mt-2 text-slate-500">Describe anything — get a studio-quality image in seconds.</p>
        </div>

        {/* Generator card */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="p-6 sm:p-8">
            {/* Prompt */}
            <div className="mb-5">
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Prompt
                <span className="ml-1.5 text-xs font-normal text-slate-400">Describe the image you want to create</span>
              </label>
              <textarea
                ref={textareaRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) void handleGenerate();
                }}
                placeholder="e.g. A futuristic city skyline at dusk with flying cars and neon signs…"
                rows={4}
                className="w-full resize-y rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-violet-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-violet-200 transition"
              />
              <p className="mt-1.5 text-right text-xs text-slate-400">{prompt.length} chars · Ctrl+Enter to generate</p>
            </div>

            {/* Suggestions */}
            <div className="mb-5">
              <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-400">Try a suggestion</p>
              <div className="flex flex-wrap gap-2">
                {PROMPT_SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => handleSuggestion(s)}
                    className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600 transition hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700"
                  >
                    {s.slice(0, 48)}…
                  </button>
                ))}
              </div>
            </div>

            {/* Options row */}
            <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
              {/* Model */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">Model</label>
                <div className="flex overflow-hidden rounded-xl border border-slate-200">
                  {(["sdxl-turbo", "sdxl-base"] as Model[]).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setModel(m)}
                      className={`flex-1 py-2 text-xs font-semibold transition ${
                        model === m
                          ? "bg-slate-900 text-white"
                          : "bg-white text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {m === "sdxl-turbo" ? "Turbo" : "Base"}
                    </button>
                  ))}
                </div>
                <p className="mt-1 text-[11px] text-slate-400">
                  {model === "sdxl-turbo" ? "Faster — great for drafts" : "Higher detail — takes longer"}
                </p>
              </div>

              {/* Size */}
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">Size</label>
                <div className="flex flex-wrap gap-2">
                  {SIZES.map((s) => (
                    <button
                      key={s.label}
                      type="button"
                      onClick={() => setSize(s)}
                      className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                        size.label === s.label
                          ? "border-violet-400 bg-violet-50 text-violet-700"
                          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-4 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                <svg xmlns="http://www.w3.org/2000/svg" className="mt-0.5 h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span>{error}</span>
                <button type="button" onClick={() => setError(null)} className="ml-auto shrink-0 text-red-400 hover:text-red-600">✕</button>
              </div>
            )}

            {/* Generate button */}
            <button
              type="button"
              disabled={!prompt.trim() || loading}
              onClick={() => void handleGenerate()}
              className="flex w-full items-center justify-center gap-2.5 rounded-xl bg-violet-600 py-3.5 text-sm font-bold text-white shadow-sm transition hover:bg-violet-700 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <>
                  <svg className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Generating… this may take up to 30 seconds
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                  Generate Image
                </>
              )}
            </button>
          </div>
        </div>

        {/* Results */}
        {results.length > 0 && (
          <div className="mt-10">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">
                Generated Images
                <span className="ml-2 rounded-full bg-slate-100 px-2.5 py-0.5 text-sm font-semibold text-slate-500">{results.length}</span>
              </h2>
              <button
                type="button"
                onClick={() => setResults([])}
                className="text-xs text-slate-400 hover:text-red-500 transition"
              >
                Clear all
              </button>
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {results.map((r, idx) => (
                <div
                  key={r.ts}
                  className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:shadow-md"
                >
                  {/* Image */}
                  <div className="relative bg-slate-100" style={{ aspectRatio: `${r.url ? "1/1" : "1/1"}` }}>
                    <img
                      src={r.url}
                      alt={r.prompt}
                      className="h-full w-full object-contain"
                      onError={(e) => { (e.currentTarget as HTMLImageElement).src = ""; }}
                    />
                    {/* Model badge */}
                    <span className="absolute left-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-semibold text-white backdrop-blur-sm">
                      {r.model}
                    </span>
                  </div>

                  {/* Meta + actions */}
                  <div className="p-4">
                    <p className="mb-3 line-clamp-2 text-sm text-slate-600">{r.prompt}</p>
                    <button
                      type="button"
                      onClick={() => handleDownload(r.url, idx)}
                      className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-slate-900 py-2 text-xs font-semibold text-white transition hover:bg-slate-700"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {results.length === 0 && !loading && (
          <div className="mt-10 flex flex-col items-center gap-3 rounded-2xl border border-dashed border-slate-200 py-16 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-violet-50">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-slate-500">Your generated images will appear here</p>
            <p className="text-xs text-slate-400">Type a prompt above and click Generate</p>
          </div>
        )}
      </div>
    </main>
  );
}
